/**
 * Ein Material (oder eine Maschine) aus Sicht der Lieferanten — die zweite
 * Perspektive der Lieferantenseite (Benjamin, 25.09.2026: „anders herum die
 * Materialien anzeigen und Lieferanten zuordnen — aus beiden Perspektiven
 * das System pflegen").
 *
 * Wer es liefern kann (★ = Standard, im Projekt vorausgewählt), mit
 * Artikelnummer und Preis; Lieferant hinzufügen per Suche; die Projekte, die es
 * brauchen. Oben Name, Einheit, Art und Kategorie — änderbar.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { fehlerText } from "@vimaana/ui";

import {
  useMaterialAendern, useMaterialBlatt, useMaterialStammlisten, useZuordnung,
  type MaterialArt, type MaterialBlattDaten, type MaterialLieferant,
} from "../../hooks/useLieferanten";
import { telLink } from "../../lib/notizen";
import LieferantSuche from "./LieferantSuche";
import "../../styles/lieferanten.css";

const eur = (n: number | null) => (n == null ? "" : n.toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

export default function MaterialDetail({ materialId, onClose, onLieferant }: {
  materialId: string; onClose?: () => void; onLieferant: (id: string) => void;
}) {
  const blatt = useMaterialBlatt(materialId);
  const d = blatt.data;
  if (blatt.isPending) return <p className="lf-leer">Lade …</p>;
  if (blatt.isError || !d) return <p className="lf-fehler">{fehlerText(blatt.error)}</p>;
  return (
    <div className="lf-detail">
      <header className="lf-detail-kopf">
        <div>
          <p className="lf-detail-ueber">{d.art === "maschine" ? "Maschine" : "Material"}</p>
          <h2>{d.name}</h2>
        </div>
        {onClose && <button type="button" className="lf-knopf lf-detail-zu" onClick={onClose} aria-label="Schließen">×</button>}
      </header>
      <StammKarte d={d} />
      <LieferantenKarte d={d} onLieferant={onLieferant} />
      <section className="lf-abschnitt">
        <div className="lf-abschnitt-kopf"><h3>Projekte</h3></div>
        {d.projekte.length === 0 ? <p className="lf-leer">Noch in keinem Projekt.</p> : (
          <ul className="lf-liste">
            {d.projekte.map((p) => (
              <li key={p.id} className={"lf-zeile" + (p.laufend ? "" : " ist-alt")}>
                <Link to={`/projekte/${p.id}`} className="lf-zeile-haupt">
                  <span className="lf-zeile-name">{p.title || "Projekt"}</span>
                  <span className="lf-zeile-info">{p.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StammKarte({ d }: { d: MaterialBlattDaten }) {
  const aendern = useMaterialAendern(d.id);
  const listen = useMaterialStammlisten();
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(d.name);
  const [einheit, setEinheit] = useState(d.einheit);
  const [kategorie, setKategorie] = useState(d.kategorie);
  const [art, setArt] = useState<MaterialArt>(d.art);
  const kategorieLabel = listen.data?.kategorien.find((k) => k.slug === d.kategorie)?.label || d.kategorie;
  if (!offen) {
    return (
      <section className="lf-abschnitt">
        <div className="lf-abschnitt-kopf">
          <h3>Stammdaten</h3>
          <button type="button" className="lf-knopf" onClick={() => { setName(d.name); setEinheit(d.einheit); setKategorie(d.kategorie); setArt(d.art); setOffen(true); }}>Bearbeiten</button>
        </div>
        <div className="lf-fakten">
          <span><small>Art</small>{d.art === "maschine" ? "Maschine" : "Material"}</span>
          <span><small>Einheit</small>{d.einheit || "—"}</span>
          {d.art !== "maschine" && <span><small>Kategorie</small>{kategorieLabel || "—"}</span>}
        </div>
      </section>
    );
  }
  return (
    <section className="lf-abschnitt">
      <div className="lf-form">
        <div className="lf-art-wahl" role="group" aria-label="Art">
          {(["material", "maschine"] as MaterialArt[]).map((a) => (
            <button key={a} type="button" className={"lf-knopf" + (art === a ? " ist-an" : "")} aria-pressed={art === a} onClick={() => setArt(a)}>
              {a === "maschine" ? "Maschine" : "Material"}
            </button>
          ))}
        </div>
        <label className="lf-label">Name<input className="lf-feld" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <div className="lf-form-zwei">
          <label className="lf-label">Einheit
            <select className="lf-feld" value={einheit} onChange={(e) => setEinheit(e.target.value)}>
              <option value="">—</option>
              {Array.from(new Set([...(listen.data?.einheiten ?? []), einheit].filter(Boolean))).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
          <label className="lf-label">Kategorie
            <select className="lf-feld" value={kategorie} onChange={(e) => setKategorie(e.target.value)} disabled={art === "maschine"}>
              <option value="">—</option>
              {(listen.data?.kategorien ?? []).map((k) => <option key={k.slug} value={k.slug}>{k.label}</option>)}
            </select>
          </label>
        </div>
        {aendern.isError && <div className="lf-fehler">{fehlerText(aendern.error)}</div>}
        <div className="lf-knoepfe">
          <button type="button" className="lf-knopf" onClick={() => setOffen(false)}>Abbrechen</button>
          <button type="button" className="lf-knopf is-primaer" disabled={!name.trim() || aendern.isPending}
            onClick={() => aendern.mutate({ name: name.trim(), einheit, kategorie: art === "maschine" ? "" : kategorie, art }, { onSuccess: () => setOffen(false) })}>
            {aendern.isPending ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </div>
    </section>
  );
}

function LieferantenKarte({ d, onLieferant }: { d: MaterialBlattDaten; onLieferant: (id: string) => void }) {
  const z = useZuordnung();
  const [neu, setNeu] = useState(0);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const fehler = z.setzen.error || z.entfernen.error || z.standard.error;
  return (
    <section className="lf-abschnitt">
      <div className="lf-abschnitt-kopf"><h3>Lieferanten{d.lieferanten.length ? ` (${d.lieferanten.length})` : ""}</h3></div>
      <p className="lf-hinweis">★ = Standard: im Projekt ist er vorausgewählt. Hinzufügen per Suche — der erste wird von selbst Standard.</p>
      {fehler && <div className="lf-fehler">{fehlerText(fehler)}</div>}
      <LieferantSuche
        key={neu}
        nurBeiEingabe
        autoFokus={false}
        platzhalter="Lieferant hinzufügen …"
        ausser={d.lieferanten.map((l) => l.id)}
        onWahl={(fid) => z.setzen.mutate({ firmaId: fid, materialId: d.id }, { onSuccess: () => setNeu((n) => n + 1) })}
      />
      {d.lieferanten.length === 0 && <p className="lf-leer">Noch niemand hinterlegt, der es liefert.</p>}
      <ul className="lf-liste">
        {d.lieferanten.map((l) => bearbeitet === l.id ? (
          <li key={l.id}><ZuordnungForm materialId={d.id} l={l} onFertig={() => setBearbeitet(null)} /></li>
        ) : (
          <li key={l.id} className="lf-zeile">
            <button type="button" className={"lf-stern" + (l.standard ? " ist-an" : "")} aria-pressed={l.standard}
              title={l.standard ? "Standard — antippen, um es zu lösen" : "Zum Standard machen"}
              disabled={z.standard.isPending}
              onClick={() => z.standard.mutate({ firmaId: l.id, materialId: d.id, standard: !l.standard })}>
              {l.standard ? "★" : "☆"}
            </button>
            <button type="button" className="lf-zeile-haupt" onClick={() => onLieferant(l.id)} title="Lieferant öffnen">
              <span className="lf-zeile-name">{l.name}</span>
              <span className="lf-zeile-info">
                {[l.artikelnummer ? `Art.-Nr. ${l.artikelnummer}` : "", l.preis_netto != null ? `${eur(l.preis_netto)} netto` : "", l.notiz, l.adresse].filter(Boolean).join(" · ")}
              </span>
            </button>
            <div className="lf-zeile-tat">
              {l.telefon && <a className="lf-knopf" href={telLink(l.telefon)} title={`${l.telefon} anrufen`}>✆</a>}
              <button type="button" className="lf-knopf" onClick={() => setBearbeitet(l.id)}>Ändern</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ZuordnungForm({ materialId, l, onFertig }: { materialId: string; l: MaterialLieferant; onFertig: () => void }) {
  const z = useZuordnung();
  const [artikelnummer, setArtikelnummer] = useState(l.artikelnummer);
  const [preis, setPreis] = useState(l.preis_netto == null ? "" : String(l.preis_netto).replace(".", ","));
  const [notiz, setNotiz] = useState(l.notiz);
  const [loeschen, setLoeschen] = useState(false);
  const preisZahl = preis.trim() ? Number(preis.replace(/\./g, "").replace(",", ".")) : null;
  const preisOk = preisZahl === null || (Number.isFinite(preisZahl) && preisZahl >= 0);
  return (
    <div className="lf-form">
      <div className="lf-zeile-name">{l.name}</div>
      <div className="lf-form-zwei">
        <label className="lf-label">Artikelnummer<input className="lf-feld" value={artikelnummer} onChange={(e) => setArtikelnummer(e.target.value)} /></label>
        <label className="lf-label">Preis netto<input className="lf-feld" inputMode="decimal" value={preis} onChange={(e) => setPreis(e.target.value)} placeholder="z. B. 12,50" /></label>
      </div>
      <label className="lf-label">Notiz<input className="lf-feld" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Lieferzeit, Mindestmenge …" /></label>
      {!preisOk && <div className="lf-fehler">Der Preis ist keine Zahl.</div>}
      {(z.setzen.error || z.entfernen.error) && <div className="lf-fehler">{fehlerText(z.setzen.error || z.entfernen.error)}</div>}
      <div className="lf-knoepfe">
        {loeschen ? (
          <button type="button" className="lf-knopf is-gefahr" onClick={() => z.entfernen.mutate({ firmaId: l.id, materialId }, { onSuccess: onFertig })}>
            {l.standard ? "Entfernen (ist Standard!)" : "Wirklich entfernen"}
          </button>
        ) : (
          <button type="button" className="lf-knopf is-gefahr" onClick={() => setLoeschen(true)}>Entfernen</button>
        )}
        <span className="lf-luecke" />
        <button type="button" className="lf-knopf" onClick={onFertig}>Abbrechen</button>
        <button type="button" className="lf-knopf is-primaer" disabled={!preisOk || z.setzen.isPending}
          onClick={() => z.setzen.mutate({ firmaId: l.id, materialId, artikelnummer, preis_netto: preisZahl, notiz }, { onSuccess: onFertig })}>
          {z.setzen.isPending ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}
